import { useEffect, useState } from 'react';
import { planRowDate } from '../utils/planRowDate';
import MealImage from '../components/MealImage';
import { motion } from 'framer-motion';
import { Check, X, RefreshCw, Plus, CheckCircle, Clock, XCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { mealAPI, mealPlanAPI } from '../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { trackLogFoodAdd, trackLogFoodRemove, trackMealRemoveFromDay } from '@/lib/posthog';
import { getTodayString } from '../utils/dateUtils';
import { useLatestRequest } from '../utils/requestLifecycle';
import LoadErrorCard from '../components/LoadErrorCard';

const DailyPlan = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(getTodayString());
  const [plans, setPlans] = useState([]);
  const [mealDetails, setMealDetails] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const dayLoad = useLatestRequest();   // a newer date aborts/ignores an older load
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading

  const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

  useEffect(() => {
    fetchDailyPlan();
  }, [date]);

  const fetchDailyPlan = async () => {
    const req = dayLoad.begin();
    const opts = { signal: req.signal };
    setLoading(true);
    setLoadError(null);
    console.log('[DailyPlan] Fetching plan for date:', date);
    
    try {
      const [plansResponse, summaryResponse] = await Promise.all([
        mealPlanAPI.getDailyPlan(date, opts),
        mealPlanAPI.getDailySummary(date, opts)
      ]);
      if (!req.isCurrent()) return;
      
      console.log('[DailyPlan] Plans fetched:', plansResponse.data.length);
      console.log('[DailyPlan] Summary fetched:', summaryResponse.data);
      
      setPlans(plansResponse.data);
      setSummary(summaryResponse.data);

      // Fetch meal details in batches to avoid rate limiting
      if (plansResponse.data.length > 0) {
        const detailsMap = {};
        const uniqueMealIds = [...new Set(plansResponse.data.map(p => p.meal_id))];
        
        // Batch fetch - max 5 at a time
        const batchSize = 5;
        for (let i = 0; i < uniqueMealIds.length; i += batchSize) {
          const batch = uniqueMealIds.slice(i, i + batchSize);
          const batchResults = await Promise.all(
            batch.map(function(mealId) {
              return mealAPI.getMeal(mealId, opts).catch(function(err) {
                console.error('[DailyPlan] Failed to fetch meal:', mealId);
                return null;
              });
            })
          );
          batchResults.forEach(function(result, idx) {
            if (result) {
              detailsMap[batch[idx]] = result.data;
            }
          });
        }
        if (!req.isCurrent()) return;
        setMealDetails(detailsMap);
        console.log('[DailyPlan] Meal details loaded:', Object.keys(detailsMap).length);
      }
    } catch (error) {
      if (!req.isCurrent()) return;
      console.error('[DailyPlan] Failed to fetch daily plan:', error);
      setLoadError(error);
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  };

  const updateStatus = async (planId, status) => {
    console.log('[DailyPlan] Updating status:', { planId, status });
    setActionLoading(planId + '-' + status);
    
    // Find meal type for tracking
    const plan = plans.find(p => p.plan_id === planId);
    const mealType = plan?.meal_type || 'Unknown';
    
    // Optimistic update
    const previousPlans = [...plans];
    setPlans(function(currentPlans) {
      return currentPlans.map(function(p) {
        if (p.plan_id === planId) {
          return { ...p, status: status };
        }
        return p;
      });
    });
    
    try {
      const response = await mealPlanAPI.updateMealPlan(planId, status);
      console.log('[DailyPlan] Update response:', response.data);
      
      // Track food logging events
      if (status === 'completed') {
        trackLogFoodAdd(mealType);
      }
      
      toast.success('Meal marked as ' + status);
      
      // Refetch to ensure consistency
      await fetchDailyPlan();
    } catch (error) {
      console.error('[DailyPlan] Failed to update status:', error);
      
      // Revert optimistic update
      setPlans(previousPlans);
      
      const errorDetail = error.response?.data?.detail || error.message;
      console.error('[DailyPlan] Error detail:', errorDetail);
      toast.error('Failed to update: ' + errorDetail);
    } finally {
      setActionLoading(null);
    }
  };

  const removeMeal = async (planId) => {
    console.log('[DailyPlan] Removing meal:', planId);
    setActionLoading(planId + '-remove');
    
    // Find meal type for tracking
    const plan = plans.find(p => p.plan_id === planId);
    const mealType = plan?.meal_type || 'Unknown';
    const wasCompleted = plan?.status === 'completed';
    
    // Optimistic update
    const previousPlans = [...plans];
    setPlans(function(currentPlans) {
      return currentPlans.filter(function(p) {
        return p.plan_id !== planId;
      });
    });
    
    try {
      const response = await mealPlanAPI.deleteMealPlan(planId);
      console.log('[DailyPlan] Delete response:', response.data);
      
      // Track appropriate event
      if (wasCompleted) {
        trackLogFoodRemove(mealType);
      } else {
        trackMealRemoveFromDay(mealType);
      }
      
      toast.success('Meal removed from plan');
      
      // Refetch to ensure consistency
      await fetchDailyPlan();
    } catch (error) {
      console.error('[DailyPlan] Failed to remove meal:', error);
      
      // Revert optimistic update
      setPlans(previousPlans);
      
      const errorDetail = error.response?.data?.detail || error.message;
      console.error('[DailyPlan] Error detail:', errorDetail);
      toast.error('Failed to remove: ' + errorDetail);
    } finally {
      setActionLoading(null);
    }
  };

  const getMealForType = (type) => {
    return plans.find(function(p) {
      return p.meal_type.toLowerCase() === type.toLowerCase();
    });
  };

  const isActionLoading = (planId, action) => {
    return actionLoading === planId + '-' + action;
  };

  return (
    <div className="min-h-screen pb-bottom-nav">
      <div className="header-safe-top px-6 pb-6 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <h1 className="font-heading font-bold text-3xl" data-testid="daily-plan-title">Daily Plan</h1>
          
          <input
            type="date"
            value={date}
            onChange={function(e) { setDate(e.target.value); }}
            data-testid="date-picker"
            className="w-full h-12 px-4 bg-secondary/30 border border-transparent focus:border-accent rounded-lg"
          />
        </motion.div>

        {/* Summary Stats */}
        {summary && summary.total_meals > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 gap-3"
            data-testid="daily-summary"
          >
            <div className="bg-card border border-border/40 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-accent" />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Completed</p>
              </div>
              <p className="text-2xl font-heading font-bold" data-testid="completed-count">
                {summary.completed_meals}/{summary.total_meals}
              </p>
            </div>

            <div className="bg-card border border-border/40 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Remaining</p>
              </div>
              <p className="text-2xl font-heading font-bold" data-testid="remaining-count">
                {summary.pending_meals}
              </p>
            </div>

            <div className="bg-card border border-border/40 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-accent" />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">AI Generated</p>
              </div>
              <p className="text-2xl font-heading font-bold" data-testid="generated-count">
                {summary.generated_meals}
              </p>
            </div>

            <div className="bg-card border border-border/40 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Plus size={16} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Manual</p>
              </div>
              <p className="text-2xl font-heading font-bold" data-testid="manual-count">
                {summary.manual_meals}
              </p>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : loadError ? (
          <LoadErrorCard error={loadError} onRetry={fetchDailyPlan} title="We couldn't load this day" testId="daily-plan-load-error" />
        ) : (
          <div className="space-y-4">
            {mealTypes.map(function(type) {
              const meal = getMealForType(type);
              const details = meal ? mealDetails[meal.meal_id] : null;
              // The row's own saved image (AI private-photo marker) when details lack one.
              const imageSrc = (details && details.image_url) || (meal && meal.image_url) || '';

              return (
                <motion.div
                  key={type}
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
                  data-testid={'meal-slot-' + type.toLowerCase()}
                >
                  <h3 className="font-heading font-semibold text-base mb-4">{type}</h3>
                  
                  {meal ? (
                    <div className="space-y-4">
                      {/* A saved row always renders (catalog details only add the
                          image); its numbers are the row's own authoritative
                          macros from GET /meal-plans/daily. */}
                      {(
                        <div 
                          className="flex gap-4 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={function() {
                            // Pass plan context so MealView can hydrate any
                            // saved edits for this logged/planned instance.
                            var params = new URLSearchParams();
                            params.set('return', '/plan/daily');
                            params.set('date', planRowDate(meal, date));
                            if (meal.plan_id) params.set('planId', meal.plan_id);
                            if (meal.status) params.set('status', meal.status);
                            if (meal.meal_type) params.set('type', meal.meal_type);
                            if (meal.scale_factor && meal.scale_factor !== 1.0) {
                              params.set('scaleFactor', meal.scale_factor);
                              if (meal.scale_label) params.set('scaleLabel', meal.scale_label);
                            }
                            navigate('/meal-view/' + meal.meal_id + '?' + params.toString());
                          }}
                        >
                          {imageSrc && (
                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                              <MealImage src={imageSrc} alt={meal.meal_name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium mb-2">{meal.meal_name}</p>
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <span>{Math.round((meal.macros && meal.macros.calories) || 0)} cal</span>
                              <span>{Math.round((meal.macros && meal.macros.protein) || 0)}g P</span>
                              <span>{Math.round((meal.macros && meal.macros.carbs) || 0)}g C</span>
                              <span>{Math.round((meal.macros && meal.macros.fat) || 0)}g F</span>
                            </div>
                            {meal.status !== 'pending' && (
                              <span className={'inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ' + (meal.status === 'completed' ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground')}>
                                {meal.status === 'completed' ? 'Completed' : 'Skipped'}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        {meal.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={function() { updateStatus(meal.plan_id, 'completed'); }}
                              disabled={isActionLoading(meal.plan_id, 'completed')}
                              data-testid={'complete-' + type.toLowerCase()}
                              className="flex-1 rounded-full bg-accent text-white hover:bg-accent/90"
                            >
                              {isActionLoading(meal.plan_id, 'completed') ? (
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                              ) : (
                                <Check size={16} className="mr-1" />
                              )}
                              Complete
                            </Button>
                            <Button
                              size="sm"
                              onClick={function() { updateStatus(meal.plan_id, 'skipped'); }}
                              disabled={isActionLoading(meal.plan_id, 'skipped')}
                              data-testid={'skip-' + type.toLowerCase()}
                              variant="outline"
                              className="flex-1 rounded-full"
                            >
                              {isActionLoading(meal.plan_id, 'skipped') ? (
                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"></span>
                              ) : (
                                <X size={16} className="mr-1" />
                              )}
                              Skip
                            </Button>
                          </>
                        )}
                        {meal.status !== 'pending' && (
                          <Button
                            size="sm"
                            onClick={function() { updateStatus(meal.plan_id, 'pending'); }}
                            disabled={isActionLoading(meal.plan_id, 'pending')}
                            data-testid={'reset-' + type.toLowerCase()}
                            variant="outline"
                            className="flex-1 rounded-full"
                          >
                            {isActionLoading(meal.plan_id, 'pending') ? (
                              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"></span>
                            ) : (
                              <RefreshCw size={16} className="mr-1" />
                            )}
                            Reset
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={function() { removeMeal(meal.plan_id); }}
                          disabled={isActionLoading(meal.plan_id, 'remove')}
                          data-testid={'remove-' + type.toLowerCase()}
                          variant="ghost"
                          className="px-3 rounded-full"
                        >
                          {isActionLoading(meal.plan_id, 'remove') ? (
                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                          ) : (
                            <X size={16} />
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={function() { navigate('/meals?type=' + type.toLowerCase() + '&date=' + date); }}
                      data-testid={'add-' + type.toLowerCase()}
                      variant="outline"
                      className="w-full rounded-full border-dashed border-2 hover:border-accent/50"
                    >
                      <Plus size={16} className="mr-2" /> Add {type}
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default DailyPlan;
